import { useEffect, useState } from "react";
import { AlertTriangle, FileDown, ScrollText, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";

// What we do (and never do) with children's data — shown verbatim to parents.
const DISCLOSURES = [
  ["Photos & voice audio are never stored", "Worksheet photos and practice recordings are processed instantly and deleted — only derived scores persist."],
  ["No ads, no selling, no third-party sharing", "Children's data is never used for advertising or sold. Sharing happens only when you explicitly invite a teacher or family member."],
  ["Verifiable parental consent (COPPA)", "Every child profile requires your attested consent per purpose. You can revoke any purpose at any time."],
  ["You own the data", "Export everything we hold per child, erase learning history, or wipe your entire family account below."],
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

// Privacy & Compliance Hub: dynamic disclosures, per-child consent records,
// proof-of-consent PDF certificate, and the full-account data purge.
export const PrivacyHub = () => {
  const [cert, setCert] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState("");
  const [showPurgeModal, setShowPurgeModal] = useState(false);

  useEffect(() => {
    api.get("/compliance/certificate").then(({ data }) => setCert(data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const { data } = await api.get("/compliance/certificate/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "consent-certificate.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Consent certificate downloaded — keep it for school or clinical records");
    } catch (e) { toast.error(errMessage(e)); }
    setBusy("");
  };

  const purge = async () => {
    setBusy("purge");
    try {
      const { data } = await api.post("/compliance/purge", { confirm });
      toast.success(`Purge complete — ${data.total_records} records across ${data.purged_children} child profile${data.purged_children === 1 ? "" : "s"} deleted`);
      setConfirm("");
      setShowPurgeModal(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(errMessage(e)); }
    setBusy("");
  };

  // 1-click consent control: flip one purpose on/off on the child's grant.
  const togglePurpose = async (child, purpose, enabled) => {
    setBusy(`p-${child.grant_id}-${purpose}`);
    try {
      const { data } = await api.patch(`/consent/grants/${child.grant_id}/purpose`,
        { purpose, enabled });
      setCert((c) => ({
        ...c,
        children: c.children.map((k) =>
          k.grant_id === child.grant_id ? { ...k, purposes: data.purposes } : k),
      }));
      toast.success(`${purpose.replace(/_/g, " ")} ${enabled ? "enabled" : "turned off"} for ${child.display_name}`);
    } catch (e) { toast.error(errMessage(e)); }
    setBusy("");
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="privacy-hub">
      <div>
        <h3 className="text-sm font-bold font-display flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-secondary" aria-hidden="true" /> Privacy &amp; Compliance Hub
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          COPPA / FERPA / GDPR-K controls for your family. Everything below is live data — not boilerplate.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-2" data-testid="privacy-disclosures">
        {DISCLOSURES.map(([title, body]) => (
          <div key={title} className="rounded-xl border border-border px-3 py-2.5">
            <p className="text-xs font-bold">{title}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{body}</p>
          </div>
        ))}
      </div>

      {cert?.children?.length > 0 && (
        <div className="space-y-2" data-testid="privacy-consent-records">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs font-bold flex items-center gap-1.5">
              <ScrollText className="h-3.5 w-3.5 text-secondary" aria-hidden="true" /> Consent records on file
            </p>
            <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs h-8"
              disabled={busy === "pdf"} onClick={downloadPdf}
              data-testid="privacy-certificate-pdf">
              <FileDown className="h-3.5 w-3.5" aria-hidden="true" /> Download consent certificate (PDF)
            </Button>
          </div>
          {cert.children.map((c) => (
            <div key={c.child_id} className="rounded-xl border border-border px-3 py-2 space-y-1"
              data-testid={`privacy-consent-${c.child_id}`}>
              <p className="text-xs font-bold">
                {c.display_name}
                <span className="font-normal text-muted-foreground">
                  {" "}· {c.grant_id ? `granted ${fmtDate(c.granted_at)} · notice v${c.notice_version} · verified via ${(c.verification_method || "").replace(/_/g, " ")}` : "no active consent"}
                </span>
              </p>
              {c.grant_id && (
                <div className="flex gap-1.5 flex-wrap">
                  {Object.entries(c.purposes || {}).map(([p, on]) => (
                    <button key={p} type="button"
                      onClick={() => togglePurpose(c, p, !on)}
                      disabled={busy === `p-${c.grant_id}-${p}`}
                      title={`Tap to turn ${on ? "off" : "on"}`}
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold border transition-colors ${
                        on ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                           : "bg-muted text-muted-foreground border-border hover:bg-slate-200"}`}
                      data-testid={`privacy-toggle-${c.child_id}-${p}`}>
                      {p.replace(/_/g, " ")}{on ? " · on" : " · off"}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-3 space-y-2"
        data-testid="privacy-danger-zone">
        <p className="text-xs font-bold text-destructive flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Delete everything
        </p>
        <p className="text-[11px] text-muted-foreground">
          Permanently erase every child profile, consent grant and learning record in your family
          account. Your parent login stays; children's data is gone forever. Type <b>DELETE</b> to arm.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type DELETE" maxLength={10}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold w-32"
            data-testid="privacy-purge-confirm-input" />
          <Button size="sm" variant="destructive" className="rounded-full text-xs h-8"
            disabled={confirm !== "DELETE" || busy === "purge"}
            onClick={() => setShowPurgeModal(true)}
            data-testid="privacy-purge-button">
            Purge All My Data
          </Button>
        </div>
      </div>

      {/* High-contrast final confirmation: oversized buttons, no ambiguity. */}
      {showPurgeModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          role="alertdialog" aria-modal="true" aria-label="Confirm permanent data deletion"
          data-testid="purge-confirm-modal">
          <div className="bg-white text-slate-900 rounded-3xl max-w-md w-full p-8 space-y-5 text-center border-4 border-red-600">
            <AlertTriangle className="h-14 w-14 text-red-600 mx-auto" aria-hidden="true" />
            <h2 className="text-2xl font-extrabold">This is permanent</h2>
            <p className="text-base font-semibold leading-relaxed">
              Every child profile, speech record, math history, streak log, story and
              consent grant in your family account will be <span className="text-red-700 font-extrabold">deleted forever</span>.
              This action <span className="underline">cannot be undone</span>.
            </p>
            <div className="space-y-3">
              <Button variant="destructive" onClick={purge} disabled={busy === "purge"}
                className="w-full h-16 rounded-2xl text-lg font-extrabold"
                data-testid="purge-modal-confirm">
                {busy === "purge" ? "Deleting everything…" : "Yes — delete everything forever"}
              </Button>
              <Button variant="outline" onClick={() => setShowPurgeModal(false)} disabled={busy === "purge"}
                className="w-full h-16 rounded-2xl text-lg font-extrabold border-2 border-slate-400"
                data-testid="purge-modal-cancel">
                No — keep our data
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
